// @flow

import * as React from 'react';

import debounce from 'lodash/debounce';

import toPairs from 'lodash/toPairs';

import { useTranslator } from '@gandi/translate';
import {
  Base,
  InputNumber,
  InputNumberInput,
  InputNumberStepDown,
  InputNumberStepUp,
} from '@gandi/ui';

export type Props = {
  id: string,
  min?: number,
  max?: number,
  count: number,
  isLoading: boolean,
  /**
   * An array of dependencies that will be passed to the input change debounce function.
   */
  callbackDependencies?: Array<mixed>,
  onChange: (value: number) => mixed,
};

type SetActive = (id: string, status: boolean) => void;

/**
 * Use this context to track debouncing on Counter.
 * This requires `CounterStateProvider` to work.
 */
export const CounterStateContext: React.Context<{
  hasActivity: boolean,
  setActive: SetActive,
} | null> = React.createContext(null);

type CounterState = {
  [id: string]: boolean,
};
/**
 * [optional] Allow to track the activity of one or more Counter. e.g.
 *
 * const TrackCounter = () => {
 *   const { hasActivity } = useContext(CounterStateContext)
 *
 *   return hasActivity ? 'debouncing' : '−';
 * }
 *
 * <CounterStateProvider>
 *   <TrackCounter />
 *   <Counter id="a" />
 *   <Counter id="b" />
 * </CounterStateProvider>
 */
export const CounterStateProvider = ({
  children,
}: {
  children: React.Node,
}): React.MixedElement => {
  const [details, dispatch] = React.useReducer<
    CounterState,
    { id: string, status: boolean },
  >((state, action) => {
    if (state[action.id] === action.status) {
      return state;
    }

    return {
      ...state,
      [action.id]: action.status,
    };
  }, {});

  const setActive = (id: string, status: boolean) => {
    dispatch({ id, status });
  };

  // find at least one active counter
  const hasActivity = !!toPairs(details).some(item => !!item[1]);

  const value = React.useMemo(
    () => ({
      setActive,
      hasActivity,
    }),
    [hasActivity],
  );

  return (
    <CounterStateContext.Provider value={value}>
      {children}
    </CounterStateContext.Provider>
  );
};

/**
 * Input number for Product's quantity.
 *
 * Optionnaly CounterStateProvider & CounterStateContext can be used to track debounce activity
 */
export default function Counter({
  id,
  min = 1,
  max,
  count,
  isLoading,
  onChange,
  callbackDependencies = [],
}: Props): React.MixedElement {
  const { __ } = useTranslator();
  const [value, setValue] = React.useState<number>(count);
  // set the current activity of the counter in a context that can be used in a parent component
  const counterState = React.useContext(CounterStateContext);

  const step = 1;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputChange = React.useCallback(
    debounce((val: number) => {
      if (counterState) {
        // no more activity
        counterState.setActive(id, false);
      }
      return onChange(val);
    }, 2000),
    [...callbackDependencies],
  );

  const handleChange = (evt: SyntheticInputEvent<>) => {
    const newValue = evt.target.valueAsNumber;
    setValue(newValue);
    if (counterState) {
      // indicate that counter is debouncing
      counterState.setActive(id, true);
    }
    debouncedInputChange(newValue);
  };

  const handleValueChange = (newValue: number) => {
    if (Number.isFinite(newValue) && newValue > 0) {
      setValue(newValue);
      if (counterState) {
        // indicate that counter is debouncing
        counterState.setActive(id, true);
      }
      debouncedInputChange(newValue);
    }
  };

  React.useEffect(() => {
    setValue(count);
    return function cleanup() {
      debouncedInputChange.cancel();
    };
  }, [count, debouncedInputChange]);

  return (
    <Base>
      <InputNumber small>
        <InputNumberStepDown
          disabled={value <= min || isLoading}
          onClick={() => handleValueChange(value - step)}
        >
          {__('Less')}
        </InputNumberStepDown>
        <InputNumberInput
          id={id}
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={isLoading}
          style={{ maxWidth: 'calc(40px + 5ch)' }}
          onChange={handleChange}
        />
        <InputNumberStepUp
          disabled={(max ? value >= max : false) || isLoading}
          onClick={() => handleValueChange(value + step)}
        >
          {__('More')}
        </InputNumberStepUp>
      </InputNumber>
    </Base>
  );
}
